import { AI_INSTRUCTIONS_VERSION, aiInstructions, type EventScope } from '../core/ai-instructions';
import { VERDICT_TITLES, type AiMoment, type AiNotes, type Verdict } from '../core/ai-notes';
import { toneOfTier, type BadgeTone } from '../core/badge';
import { CAMERA_LABELS, formatTime } from '../core/format';
import { noteTier } from '../core/ranking';

export interface AiDialogDom {
  readonly dialog: HTMLDialogElement;
  readonly notes: HTMLElement;
  readonly verdict: HTMLElement;
  readonly reason: HTMLElement;
  readonly summary: HTMLElement;
  readonly moments: HTMLOListElement;
  readonly about: HTMLElement;
  readonly outdated: HTMLElement;
  readonly empty: HTMLElement;
  readonly target: HTMLElement;
  readonly details: HTMLDetailsElement;
  readonly instructions: HTMLElement;
  readonly copyEvent: HTMLButtonElement;
  readonly copyAll: HTMLButtonElement;
}

export interface AiContext {
  readonly notes: AiNotes | undefined;
  readonly event: EventScope;
  readonly root: string;
  readonly utcOffset: number;
  readonly jump: (time: number) => void;
}

type Clipboard = Pick<globalThis.Clipboard, 'writeText'>;

const PERCENT = 100;

const VERDICT_TONES: Readonly<Record<Verdict, BadgeTone>> = {
  incident: 'incident',
  notable: 'nearby',
  routine: 'quiet',
  false_alarm: 'quiet',
};

function aboutLine(notes: AiNotes): string {
  const tags = notes.tags.length ? [`Tags: ${notes.tags.join(', ')}`] : [];
  const model = notes.model ? [`Written by ${notes.model}`] : [];
  const version = notes.version ? [`Instructions v${notes.version}`] : [];
  return [...tags, ...model, ...version].join(' · ');
}

const outdatedLine = (version: number | undefined): string =>
  `Written with older instructions (${version ? `v${version}` : 'no version'}, current v${AI_INSTRUCTIONS_VERSION}). Copy them again and ask the AI to regenerate this recording to update the notes.`;

export class AiDialog {
  private context: AiContext | undefined;

  constructor(
    private readonly dom: AiDialogDom,
    private readonly notify: (message: string) => void,
    private readonly clipboard: Clipboard | undefined,
  ) {
    dom.copyEvent.addEventListener('click', () => this.context && void this.copy(aiInstructions(this.context.event)));
    dom.copyAll.addEventListener('click', () => this.context && void this.copy(aiInstructions({ kind: 'all', root: this.context.root })));
  }

  open(context: AiContext): void {
    this.context = context;
    const { notes } = context;
    this.dom.notes.hidden = notes === undefined;
    this.dom.empty.hidden = notes !== undefined;
    this.dom.target.textContent = context.event.file;
    this.dom.instructions.textContent = aiInstructions(context.event);
    this.dom.details.open = false;
    if (notes) this.renderNotes(notes, context);
    if (!this.dom.dialog.open) this.dom.dialog.showModal();
  }

  private renderNotes(notes: AiNotes, context: AiContext): void {
    const verdict = notes.verdict;
    this.dom.verdict.hidden = verdict === undefined;
    this.dom.verdict.textContent = verdict ? VERDICT_TITLES[verdict] : '';
    this.dom.verdict.dataset['tone'] = verdict ? VERDICT_TONES[verdict] : 'quiet';
    this.dom.reason.textContent = notes.verdictReason;
    this.dom.summary.textContent = notes.summary;
    this.dom.moments.replaceChildren(...notes.moments.map((moment) => this.momentRow(moment, context)));
    this.dom.about.textContent = aboutLine(notes);
    this.dom.outdated.hidden = (notes.version ?? 0) >= AI_INSTRUCTIONS_VERSION;
    this.dom.outdated.textContent = outdatedLine(notes.version);
  }

  private momentRow(moment: AiMoment, context: AiContext): HTMLLIElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tv-ai-moment';
    button.dataset['tone'] = toneOfTier(noteTier(moment));
    const camera = moment.camera ? ` · ${CAMERA_LABELS[moment.camera]}` : '';
    const confidence = moment.confidence === undefined ? '' : ` · ${Math.round(moment.confidence * PERCENT)}% sure`;
    button.textContent = `${formatTime(moment.peak)} · ${moment.label}${camera}${confidence}`;
    button.addEventListener('click', () => {
      this.dom.dialog.close();
      context.jump(moment.peak - context.utcOffset);
    });
    const item = document.createElement('li');
    item.append(button);
    return item;
  }

  private async copy(text: string): Promise<void> {
    try {
      if (!this.clipboard) throw new Error('no clipboard');
      await this.clipboard.writeText(text);
      this.dom.dialog.close();
      this.notify('Instructions copied');
    } catch {
      this.notify('Copying was blocked. Open Instructions and copy the text from there.');
    }
  }
}
